import { toOpaqueColor } from '../utils/formatters';

// Shared geometry for every Mini Chat Dock surface — launcher pill,
// conversation panel and chat window all sit on the same edge offset and
// share one width / height / radius, so switching between them never makes
// the widget jump around or change shape.
export const DOCK_EDGE = 16;
export const DOCK_WIDTH = 340;
export const DOCK_HEIGHT = 'min(430px, 70vh)';
export const DOCK_RADIUS = '16px';

// Dock-wide surface tokens, derived from the app's own messenger tokens
// (messengerColors.js). `popoverBg` is the system's floating-surface color;
// it's only 95% opaque by design (the full page's glass effect), so
// `toOpaqueColor` strips that alpha — a small popup sitting over arbitrary
// page content must never be see-through.
export const getDockSurfaces = (c, theme) => {
  const isDark = theme === 'dark';
  return {
    popupBg: toOpaqueColor(c.popoverBg),
    // Light `inputBg` is pure white — invisible on the white popup — so text
    // fields get a soft slate fill there instead.
    fieldBg: isDark ? toOpaqueColor(c.inputBg) : '#f1f5f9',
    border: c.popoverBorder,
    divider: c.border,
    text: c.text,
    muted: c.textSecondary,
    hover: c.hoverBg,
    accent: c.myMessageBg,
    unread: '#2563eb', // matches the shared <UnreadBadge/> component's default
    shadow: isDark
      ? '0 18px 48px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.25)'
      : '0 18px 48px rgba(15, 23, 42, 0.16), 0 2px 8px rgba(15, 23, 42, 0.06)',
  };
};

// Compact-window equivalent of `getResponsiveStyles` (Message_Style.js).
// Returns objects under the EXACT SAME keys that ChatHeader / ChatInput /
// MessagesContainer / MessageBubble already expect, just sized for a small
// floating window instead of a full page — this is what lets those
// feature-complete components be reused completely unmodified in the Mini
// Chat Dock.
export const getMiniChatStyles = (c, isRTL, theme) => {
  const surfaces = getDockSurfaces(c, theme);
  const solidBg = surfaces.popupBg;
  const solidPanelBg = toOpaqueColor(c.chatBg);
  return {
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontWeight: '700',
      fontSize: '13px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      border: `2px solid ${solidBg}`,
      transition: 'all 0.2s ease',
    },
    onlineIndicator: {
      position: 'absolute',
      bottom: '0px',
      right: isRTL ? 'auto' : '0px',
      left: isRTL ? '0px' : 'auto',
      width: '11px',
      height: '11px',
      background: c.online,
      border: `2px solid ${solidBg}`,
      borderRadius: '50%',
      boxShadow: '0 1px 4px rgba(34, 197, 94, 0.4)',
    },
    backButton: {
      padding: '6px',
      background: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'none', // no mobile-style back nav inside a floating window
      alignItems: 'center',
      justifyContent: 'center',
      color: c.text,
    },
    chatHeader: {
      background: solidBg,
      borderBottom: `1px solid ${c.border}`,
      padding: '10px 12px',
      flexShrink: 0,
    },
    messagesContainer: {
      position: 'relative',
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: '8px 10px',
      backgroundColor: solidPanelBg,
      minHeight: 0,
    },
    // Smaller + tighter than the full page's bubble to fit the narrow window.
    messageBubble: {
      padding: '7px 11px',
      borderRadius: '16px',
      fontSize: '13px',
      width: 'fit-content',
      maxWidth: '100%',
      wordBreak: 'break-word',
      position: 'relative',
      lineHeight: '1.45',
    },
    // Flat bubbles — the full page's glow shadow is far too heavy at this size.
    myMessage: {
      background: c.myMessageGradient || '#2e7cba',
      color: '#ffffff',
      borderBottomRightRadius: isRTL ? '16px' : '4px',
      borderBottomLeftRadius: isRTL ? '4px' : '16px',
      marginLeft: isRTL ? '0' : 'auto',
      marginRight: isRTL ? 'auto' : '0',
      boxShadow: 'none',
      border: 'none',
    },
    theirMessage: {
      background: c.theirMessageBg,
      color: c.text,
      borderBottomRightRadius: isRTL ? '4px' : '16px',
      borderBottomLeftRadius: isRTL ? '16px' : '4px',
      marginRight: isRTL ? '0' : 'auto',
      marginLeft: isRTL ? 'auto' : '0',
      boxShadow: 'none',
      border: 'none',
    },
    inputArea: {
      background: solidBg,
      borderTop: `1px solid ${c.border}`,
      padding: '10px 12px 12px',
      flexShrink: 0,
    },
    inputContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      background: surfaces.fieldBg,
      borderRadius: '22px',
      padding: '3px 3px 3px 4px',
      border: `1px solid ${c.inputBorder}`,
    },
    iconButton: {
      padding: '6px',
      color: '#2e7cba',
      background: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    messageInput: {
      flex: 1,
      minWidth: 0,
      padding: '8px 6px',
      background: 'transparent',
      border: 'none',
      outline: 'none',
      fontSize: '13.5px',
      color: c.text,
      minHeight: '36px',
      maxHeight: '90px',
      resize: 'none',
      fontFamily: 'inherit',
      lineHeight: '1.4',
      boxSizing: 'border-box',
    },
    sendButton: {
      padding: '8px',
      background: 'linear-gradient(135deg, #2e7cba, #1a6baa)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: 'none',
      transition: 'background 0.15s ease, color 0.15s ease, opacity 0.15s ease',
    },
    // Quiet icon instead of a solid grey disc while there's nothing to send.
    sendButtonDisabled: {
      background: 'transparent',
      color: c.textSecondary,
      opacity: 0.55,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  };
};

export default getMiniChatStyles;
